<template>
  <div v-if="loaded">
    <div v-if="isEmpty(vehicleStates)">
      <span class="text-subtitle-1">
        {{ $t('24_hr_no_vehicle_sensors') }}
      </span>
    </div>
    <v-row v-else>
      <v-col>
        <v-card outlined rounded>
          <v-card-text>
            <temperature-chart
              ref="vehicle-sensor-chart"
              :height="400"
              :data="temperatureData"/>
          </v-card-text>
        </v-card>
        <v-subheader>
          {{ $t('sensor_data_from_past_24_hours_only') }}
        </v-subheader>
      </v-col>
      <v-col>
        <v-data-table
          class="vf-table"
          :headers="tableFields"
          :items="vehicleStates"
          :loading="loading"
          :loading-text="$t('loading')">
          <template #item.timestamp="row">
            <multi-time-display :time="row.item.timestamp" />
          </template>
          <template #item.freezer="row">
            <if-set :value="row.item.getField('freezer')">
              <template #default="scope">
                {{ temperatureToCustomerScale(scope.value) }} &deg;{{ customerContext.tempFormat() }}
              </template>
            </if-set>
          </template>
          <template #item.cooler="row">
            <if-set :value="row.item.getField('cooler')">
              <template #default="scope">
                {{ temperatureToCustomerScale(scope.value) }} &deg;{{ customerContext.tempFormat() }}
              </template>
            </if-set>
          </template>
          <template #item.gps="row">
            <if-set :value="row.item.getGpsCoordinatesAsString()">
              <template #default="scope">
                <a :href="`https://maps.google.com/maps?q=${scope.value}`" target="_blank">
                  <v-icon color="primary">mdi-earth</v-icon>
                  {{ scope.value }}
                </a>
              </template>
            </if-set>
          </template>
          <template #item.location="row">
            <router-link v-if="row.value" :to="`/location/${row.value.id}`">
              <v-icon color="primary">{{ row.value.vuetifyIcon }}</v-icon>{{row.value.label}}
            </router-link>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import TemperatureChart from '@/components/shared/chart/TemperatureChart.vue'
import IfSet from '@/components/shared/IfSet'
import { each, isEmpty } from 'lodash'
import Moment from 'moment'

export default {
  name: 'VehicleSensorHistory',
  components: {
    MultiTimeDisplay,
    TemperatureChart,
    IfSet
  },
  mixins: [
    CurrentUserMixin,
    TemperatureMixin
  ],
  props: {
    vehicle: Object,
  },
  data () {
    return {
      vehiclePositions: [],
      vehicleStates: [],
      loading: false,
      loaded: false,
      // This is the merged state and position data
      vehicleData: [],
      tableFields: [
        {
          value: 'timestamp',
          text: this.$t('timestamp'),
        },
        {
          value: 'freezer',
          text: this.$t('freezer_air')
        },
        {
          value: 'cooler',
          text: this.$t('cooler_air')
        },
        {
          value: 'gps',
          text: 'GPS',
          sortable: false
        },
        {
          value: 'location',
          text: this.$tc('location'),
          sortable: false
        }
      ]
    }
  },
  computed: {
    /**
     * Format the vehicle data for the temperature chart.
     * @returns Object
     */
    temperatureData () {
      const results = {
        air: {
          cooler: {
            temps: [],
            dates: []
          },
          freezer: {
            temps: [],
            dates: []
          }
        }
      }
      each(this.vehicleStates, state => {
        const cooler = state.getField('cooler')
        const freezer = state.getField('freezer')
        if (cooler) {
          results.air.cooler.temps.push(this.temperatureToCustomerScale(cooler))
          results.air.cooler.dates.push(Moment(state.timestamp).valueOf())
        }
        if (freezer) {
          results.air.freezer.temps.push(this.temperatureToCustomerScale(freezer))
          results.air.freezer.dates.push(Moment(state.timestamp).valueOf())
        }
      })
      return results
    }
  },
  methods: {
    isEmpty: isEmpty,
    async fetchVehicleStates () {
      this.loading = true
      try {
        const cutoff = Moment().subtract(24, 'hours').format()
        const positionsResp = await this.$orm('VehiclePosition').findAll({
          vehicle_id: this.vehicle.id,
          created_after: cutoff,
          sort: 'timestamp',
          expand: 'location',
          'per-page': 1000,
          fields: 'id,timestamp,location.label,location.id,location.type'
        })
        this.vehiclePositions = positionsResp.getItems()
        const statesResp = await this.$orm('VehicleData').findAll({
          vehicle_id: this.vehicle.id,
          timestamp_after: cutoff,
          'per-page': 1000,
          sort: 'timestamp'
        })
        this.vehicleStates = statesResp.getItems()
        // Merge the already-loaded vehicle position data in.
        each(this.vehiclePositions, position => {
          if (position.location) {
            each(this.vehicleStates, state => {
              if (state.timestamp === position.timestamp) {
                state.location = position.location
              }
            })
          }
        })
        this.loaded = true
      } catch (error) {
        this.handleError(error)
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    this.fetchVehicleStates()
  }
}
</script>
